import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import GeoTIFF from 'ol/source/GeoTIFF';
import CustomModalHeader from '../../../Common/customComponents/CustomModalHeader.tsx';
import { BasicModalV2, TextButton } from '../../../../components';

import BtnMoveBack from '/assets/icon/global/btn-move-back.svg';
import BtnMoveFront from '/assets/icon/global/btn-move-front.svg';
import BtnRefresh from '/assets/icon/global/btn-refresh.svg';
import BtnZoomIn from '/assets/icon/global/btn-zoom-in.svg';
import BtnZoomOut from '/assets/icon/global/btn-zoom-out.svg';

import SampleMapImage from '/assets/icon/global/sample-map-image.svg';
import { XYZ } from 'ol/source';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Icon, Stroke, Style } from 'ol/style';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { safObjectHelper } from '../../../../utils/SafObjectHelper.ts';
import { commonMsg } from 'utils/globalUtil.tsx';
import { useAsync } from 'hooks/useAsync.ts';
import _ from 'lodash';
import LineString from 'ol/geom/LineString';

interface IProps {
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  enableDrawLine?: boolean;
  onActionCallback: any;
  title?: string;
  location?: any;
  locationText?: any;
}

const WorkLocationModal = (props: IProps) => {
  const mapLeftElement = useRef<any>(null);
  const mapRightElement = useRef<any>(null);
  const mapObject = useRef<any>(null);
  const mapObject2 = useRef<any>(null);
  const mapLayer1 = useRef<any>(null);
  const mapLayer2 = useRef<any>(null);
  const history = useRef<any>([]);
  const redoStack = useRef<any>([]);
  const isInternalMove = useRef(false);
  const [locationText, setLocationText] = useState<any>('');
  const [locationJson, setLocationJson] = useState<{}>({});
  const [points, setPoints] = useState<any[]>([]);
  const [lines, setLines] = useState<number[][][]>([]);
  const [extent, setExtent] = useState<number[]>();
  const { callAsync, showAlarm, closeAlarm } = useAsync();
  let activeLineFeature: Feature<LineString> | null = null;
  useEffect(() => {
    if (!props.isModalOpen) return;
    //
    setPoints(props.location?.points);
    setLines(props.location?.lines);
    setExtent(props.location?.extent);
    setLocationText(props.locationText);
    const timer = setTimeout(() => {
      //console.log(mapLeftElement.current);
      const sharedView = new View({
        projection: 'EPSG:4326',
        center: [104.16636802395145, 1.3502279683562692],
        zoom: 15.5,
        maxZoom: 21
      });
      //
      const googleSatelliteLayer = new TileLayer({
        source: new XYZ({
          url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'
        })
      });
      const tiffSource = new GeoTIFF({
        sources: [{ url: '/maps/saf_modified_tiled_v1.1.tif' }]
      });
      const marker1Layer = new VectorLayer({
        source: new VectorSource(),
        style: [
          new Style({
            image: new Icon({
              src: '/assets/icon/map/point.svg',
              scale: 0.5
            })
          }),
          new Style({
            stroke: new Stroke({
              color: '#4444ff',
              width: 7,
              lineCap: 'round',
              lineJoin: 'round'
            }),
            zIndex: -1
          })
        ]
      });
      const marker2Layer = new VectorLayer({
        source: new VectorSource(),
        style: [
          new Style({
            image: new Icon({
              src: '/assets/icon/map/point.svg',
              scale: 0.5
            }),
            zIndex: 0
          }),
          new Style({
            image: new Icon({
              anchor: [0.5, 1],
              src: '/assets/icon/map/marker.svg',
              scale: 0.5
            }),
            zIndex: 1
          }),
          new Style({
            stroke: new Stroke({
              color: '#4444ff',
              width: 7,
              lineCap: 'round',
              lineJoin: 'round'
            }),
            zIndex: -1
          })
        ]
      });
      //
      const leftMap = new Map({
        target: mapLeftElement.current,
        layers: [
          new WebGLTileLayer({
            source: tiffSource
          }),
          marker2Layer
        ],
        view: sharedView
      });
      const rightMap = new Map({
        target: mapRightElement.current,
        layers: [googleSatelliteLayer, marker1Layer],
        view: sharedView
      });
      mapObject.current = leftMap;
      mapObject2.current = rightMap;
      mapLayer1.current = marker1Layer;
      mapLayer2.current = marker2Layer;
      const handleMapClick = (event: any) => {
        if (props.enableDrawLine) {
          const coordinate = event.coordinate;
          if (!activeLineFeature) {
            const map = event.map;
            const pixel = event.pixel;
            const feature = map.forEachFeatureAtPixel(pixel, (f: any) => f);
            if (feature) {
              // 이미 마커가 있다면 삭제
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              marker1Layer.getSource().removeFeature(feature as Feature);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              marker2Layer.getSource().removeFeature(feature as Feature);
              updateLinesFromLayer();
            } else {
              activeLineFeature = new Feature({
                geometry: new LineString([coordinate, coordinate])
              });
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              marker1Layer.getSource().addFeature(activeLineFeature);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              marker2Layer.getSource().addFeature(activeLineFeature);
            }
          } else {
            const geometry = activeLineFeature.getGeometry();
            if (geometry) {
              const coords = geometry.getCoordinates();
              geometry.setCoordinates([...coords, coordinate]);
            }
          }
        } else {
          const map = event.map;
          const pixel = event.pixel;
          const coordinate = event.coordinate;
          // 클릭한 지점에 이미 피처(마커)가 있는지 확인
          const feature = map.forEachFeatureAtPixel(pixel, (f: any) => f);
          if (feature) {
            // 이미 마커가 있다면 삭제
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            marker1Layer.getSource().removeFeature(feature as Feature);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            marker2Layer.getSource().removeFeature(feature as Feature);
            console.log('마커 삭제됨');
          } else {
            // 마커가 없다면 새로 생성
            const newFeature = new Feature({
              geometry: new Point(coordinate)
            });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            marker1Layer.getSource().addFeature(newFeature);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            marker2Layer.getSource().addFeature(newFeature);
          }
          var _points = [];
          var source = marker2Layer.getSource();
          var features = source?.getFeatures();
          for (var i in features) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            var f = features[i];
            _points.push(f.getGeometry().getCoordinates());
          }
          setPoints(_points);
        }
      };
      leftMap.on('click', handleMapClick);
      rightMap.on('click', handleMapClick);
      const handleMapDblClick = (event: any) => {
        updateLinesFromLayer();
        if (activeLineFeature != null) {
          activeLineFeature = null;
          return false;
        }
      };
      const updateLinesFromLayer = () => {
        const source = marker1Layer.getSource();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const features = source.getFeatures();
        const allLineCoords = features
          .map((feature: any) => {
            const geometry = feature.getGeometry();
            if (geometry && geometry.getType() === 'LineString') {
              return geometry.getCoordinates();
            }
            return null;
          })
          .filter((coords: any) => coords !== null); // null 값 제거
        //console.log(212123333, allLineCoords);
        setLines(allLineCoords);
      };
      if (props.enableDrawLine) {
        leftMap.on('dblclick', handleMapDblClick);
        rightMap.on('dblclick', handleMapDblClick);
      }
      rightMap.on('moveend', function () {
        if (rightMap) {
          const _extent = rightMap.getView().calculateExtent(rightMap.getSize());
          console.log(_extent);
          setExtent(_extent);
        }
        //
        if (!isInternalMove.current) {
          const view = leftMap.getView();
          const currentState = {
            center: view.getCenter(),
            zoom: view.getZoom()
          };
          history.current.push(currentState);
          redoStack.current = [];
        }
        isInternalMove.current = false;
      });
      if (props.location) {
        rightMap.getView().fit(props.location.extent, {
          padding: [0, 0, 0, 0]
        });
        //
        var source1 = marker1Layer.getSource();
        var source2 = marker2Layer.getSource();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        source1.clear();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        source2.clear();
        var points = props.location.points;
        var features = [];
        for (var i in points) {
          const newFeature = new Feature({
            geometry: new Point(points[i])
          });
          features.push(newFeature);
        }
        //
        var lines = props.location.lines;
        if (lines) {
          lines.forEach((lineCoords: any) => {
            const feature = new Feature({
              geometry: new LineString(lineCoords)
            });
            features.push(feature);
          });
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        source1.addFeatures(features);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        source2.addFeatures(features);
      }
      setTimeout(() => {
        leftMap.updateSize();
        rightMap.updateSize();
      }, 100);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window.___map = rightMap;
    }, 100);
    return () => clearTimeout(timer);
  }, [props.isModalOpen]);
  useEffect(() => {
    var data = {
      extent: extent,
      points: points,
      lines: lines
    };
    //var str = safObjectHelper.mapDataToStr(data);
    //console.log(1212, data);
    setLocationJson(data);
  }, [points, lines, extent]);

  const modalClose = () => {
    props.setIsModalOpen(false);
  };
  const fn_zoom_out = () => {
    const view = mapObject.current.getView();
    const zoom = view.getZoom();
    view.animate({ zoom: zoom - 1, duration: 250 });
  };
  const fn_zoom_in = () => {
    const view = mapObject.current.getView();
    const zoom = view.getZoom();
    view.animate({ zoom: zoom + 1, duration: 250 });
  };
  const fn_map_init = () => {
    const view = mapObject.current.getView();
    view.animate({
      center: [104.16636802395145, 1.3502279683562692],
      zoom: 15.5,
      duration: 500
    });
    if (mapLayer1.current) {
      var source1 = mapLayer1.current.getSource();
      source1.clear();
    }
    if (mapLayer2.current) {
      var source2 = mapLayer2.current.getSource();
      source2.clear();
    }
  };
  const fn_map_next = () => {
    if (redoStack.current.length === 0) return;
    isInternalMove.current = true;
    const nextState: any = redoStack.current.pop();
    if (nextState) {
      history.current.push(nextState);
      mapObject.current.getView().animate({
        center: nextState.center,
        zoom: nextState.zoom,
        duration: 400
      });
    }
  };
  const fn_map_prev = () => {
    if (history.current.length <= 1) return;
    isInternalMove.current = true;
    const currentState = history.current.pop();
    redoStack.current.push(currentState);
    const prevState: any = history.current[history.current.length - 1];
    mapObject.current.getView().animate({
      center: prevState.center,
      zoom: prevState.zoom,
      duration: 400
    });
  };
  useEffect(() => {
    if (mapObject2.current) {
      safObjectHelper.addOdmOrthophotoMap(mapObject2.current, 1);
    }
  }, [mapObject2.current]);
  return (
    <BasicModalV2 open={props.isModalOpen} onClose={() => props.setIsModalOpen(false)}>
      <CustomModalHeader title={props.title || 'Work Location'} modalClose={modalClose} />
      <div
        className={'custom-modal-body work-location-modal'}
        style={{ width: '1200px', height: '700px' }}
      >
        <div className={'button-box'}>
          <div className={'flex-row'}>
            <button className={'btn-group btn-group-left'} onClick={fn_zoom_out}>
              <img src={BtnZoomOut} />
            </button>
            <button className={'btn-group btn-group-right'} onClick={fn_zoom_in}>
              <img src={BtnZoomIn} />
            </button>
          </div>
          <div className={'flex-row'}>
            <button className={'btn-group btn-group-single'} onClick={fn_map_init}>
              <img src={BtnRefresh} />
            </button>
          </div>
          <div className={'flex-row'}>
            <button className={'btn-group btn-group-left'} onClick={fn_map_prev}>
              <img src={BtnMoveFront} />
            </button>
            <button className={'btn-group btn-group-right'} onClick={fn_map_next}>
              <img src={BtnMoveBack} />
            </button>
          </div>
        </div>
        <div className={'image-box'}>
          <div className={'image-box-left'} id={'left-map'} ref={mapLeftElement}></div>
          <div className={'image-box-right'} id={'right-map'} ref={mapRightElement}></div>
        </div>
        <div className={'input-box'}>
          <div className={'input-label'}>
            <span>Work Location</span>
          </div>
          <div className={'input-value'}>
            <input
              type={'text'}
              maxLength={100}
              value={locationText || ''}
              onChange={(e: any) => {
                setLocationText(e.target.value);
              }}
            />
          </div>
          <div>
            <TextButton
              variant="contained"
              style={{ height: '40px' }}
              onClick={() => {
                if (_.isEmpty(locationText)) {
                  commonMsg.alert(
                    showAlarm,
                    closeAlarm,
                    'Warning',
                    'Please enter the main work location.',
                    null
                  );
                  return;
                }
                props.onActionCallback(locationJson, locationText);
              }}
            >
              Save
            </TextButton>
          </div>
        </div>
        {props.enableDrawLine && (
          <div className={'work-location-guide'}>
            Click to set the starting point, then continue clicking to draw connected lines.
            Double-click to finish drawing.
          </div>
        )}
      </div>
    </BasicModalV2>
  );
};

export default WorkLocationModal;
